import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import {
  FlexColumn,
  SushiModal,
  Button as PrimaryButton,
} from "../Common/styles.common";
import Datepicker, { MONTHS } from "../../shared/Datepicker/Datepicker";
import Dropdown from "../../shared/Dropdown/Dropdown";
import { downloadEmbeddedUpcLabelPdf, downloadEmbeddedUpcLabelPng } from "./embeddedUpcLabel";
import useVendorStore from "../HomePage/useVendorStore";
import styles from "./GenerateUpcModal.module.scss";

const SCHEMA_TYPE = "uem";
const ENCODING_TYPE = "raw";
const DATE_FORMAT = "YYYY-MM-DD";

const FORMAT_PDF = "pdf";
const FORMAT_PNG = "png";
const FORMAT_OPTIONS = [
  { value: FORMAT_PDF, label: "PDF" },
  { value: FORMAT_PNG, label: "PNG" },
];

const CalendarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <path d="M3 9h18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const ChevronIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const FloatingDateInput = forwardRef(
  ({ value, onClick, label }, ref) => {
    const filled = !!value;
    return (
      <div
        ref={ref}
        className={`${styles?.fieldBox} ${filled ? styles?.floating : ""}`}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onClick && onClick(e);
        }}
      >
        <span className={styles?.fieldLabel}>{label}</span>
        {filled ? <span className={styles?.fieldValue}>{value}</span> : null}
        <span className={styles?.iconRight} aria-hidden>
          <CalendarIcon />
        </span>
      </div>
    );
  }
);
FloatingDateInput.displayName = "FloatingDateInput";

const FloatingSelect = ({ value, onChange, options, label, disabled }) => {
  const filled = value !== "" && value !== null && value !== undefined;
  const selected = options.find((o) => String(o.value) === String(value));
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  return (
    <div className={styles?.selectWrapper} ref={wrapRef}>
      <div
        className={`${styles?.fieldBox} ${filled ? styles?.floating : ""} ${open ? styles?.focused : ""} ${disabled ? styles?.fieldBoxDisabled : ""}`}
        onClick={() => { if (!disabled) setOpen((p) => !p); }}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={open}
        onKeyDown={(e) => {
          if (!disabled && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setOpen((p) => !p);
          }
        }}
      >
        <span className={styles?.fieldLabel}>{label}</span>
        {filled ? (
          <span className={styles?.fieldValue}>{selected?.label}</span>
        ) : null}
        <span
          className={`${styles?.chevronIcon} ${open ? styles?.chevronOpen : ""}`}
          aria-hidden
        >
          <ChevronIcon />
        </span>
      </div>
      {open && !disabled && (
        <ul className={styles?.dropdownMenu} role="listbox">
          {options.map((o) => {
            const isSelected = String(o.value) === String(value);
            return (
              <li
                key={`${o.value}`}
                className={`${styles?.dropdownItem} ${isSelected ? styles?.dropdownItemSelected : ""}`}
                role="option"
                aria-selected={isSelected}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(o.value);
                  setOpen(false);
                }}
              >
                {o.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const GenerateUpcModal = ({ visible, onClose, item, poId }) => {
  const variants = item?.active_product_variants || [];
  const hasMultipleVariants = variants.length > 1;
  const generateEmbeddedUpc = useVendorStore((state) => state.generateEmbeddedUpc);

  const [packedOnDate, setPackedOnDate] = useState(null);
  const [viewDate, setViewDate] = useState(() => new Date());
  const [pickerOpen, setPickerOpen] = useState(false);
  const variantOptions = useMemo(() =>
    variants
      .map((v, idx) => ({ value: idx, label: `${v.upc} - ${v.external_variant_description}` }))
      .filter((o) => !!o.label?.trim()),
    [variants]
  );

  const [selectedVariantIdx, setSelectedVariantIdx] = useState(
    variantOptions.length === 1 ? variantOptions[0].value : ""
  );
  const [downloadFormat, setDownloadFormat] = useState(FORMAT_PDF);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (visible) {
      setPackedOnDate(null);
      setViewDate(new Date());
      setPickerOpen(false);
      setSelectedVariantIdx(variantOptions.length === 1 ? variantOptions[0].value : "");
      setDownloadFormat(FORMAT_PDF);
      setSubmitting(false);
      setError("");
    }
  }, [visible, item?.id, variantOptions]);

  const selectedVariant =
    selectedVariantIdx === "" ? null : variants[Number(selectedVariantIdx)];

  const expiryDateStr = useMemo(() => {
    const shelfLifeDays = Number(item?.shelf_life) || 0;
    if (!packedOnDate || shelfLifeDays < 0) return "";
    return moment(packedOnDate)
      .add(shelfLifeDays, "days")
      .format(DATE_FORMAT);
  }, [packedOnDate, item?.shelf_life]);

  const { packingMinDate, packingMaxDate } = useMemo(() => {
    const today = moment().startOf("day");
    const shelfLifeDays = Number(item?.shelf_life) || 0;
    return {
      packingMinDate: shelfLifeDays >= 0
        ? today.clone().subtract(Math.max(0, shelfLifeDays - 1), "days").toDate()
        : undefined,
      packingMaxDate: today.clone().add(1, "day").toDate(),
    };
  }, [item?.shelf_life]);

  const yearOptions = useMemo(() => {
    const minYear = packingMinDate
      ? packingMinDate.getFullYear()
      : new Date().getFullYear() - 5;
    const maxYear = packingMaxDate
      ? packingMaxDate.getFullYear()
      : new Date().getFullYear();
    const years = [];
    for (let y = minYear; y <= maxYear; y += 1) {
      years.push({ value: y, label: String(y) });
    }
    return years;
  }, [packingMinDate, packingMaxDate]);

  const monthOptions = useMemo(
    () => MONTHS.map((m, idx) => ({ value: idx, label: m })),
    []
  );

  const isValid = !!packedOnDate && !!selectedVariant?.upc;

  const handleGenerate = async () => {
    if (!isValid || submitting) return;
    setSubmitting(true);
    setError("");
    const packedOnStr = moment(packedOnDate).format(DATE_FORMAT);
    const vendorId =
      window.localStorage.getItem("outlet_id") ||
      window.localStorage.getItem("outletId") ||
      "";
    const payload = {
      schema_type: SCHEMA_TYPE,
      encoding_type: ENCODING_TYPE,
      upc: String(selectedVariant?.upc),
      mrp: String(selectedVariant?.external_variant_mrp ?? "0"),
      expiry_date: expiryDateStr,
      manufacturing_date: packedOnStr,
      vendor_id: String(vendorId || ""),
    };

    generateEmbeddedUpc(
      payload,
      async (data) => {
        try {
          const qrCodeBase64 = data?.qr_code;
          if (!qrCodeBase64) {
            throw new Error("No qr_code returned from server");
          }
          const labelArgs = {
            qrCodeBase64,
            upc: String(selectedVariant?.upc),
            product: item?.product_name || "",
            packedOn: packedOnStr,
            expiresOn: expiryDateStr,
            fileName: `upc_${poId ?? ""}_${item?.product_number ?? ""}_${packedOnStr.replace(/-/g, "")}`,
          };
          if (downloadFormat === FORMAT_PNG) {
            await downloadEmbeddedUpcLabelPng(labelArgs);
          } else {
            await downloadEmbeddedUpcLabelPdf(labelArgs);
          }
          onClose && onClose();
        } catch (e) {
          setError(e?.message || "Failed to generate qr");
        } finally {
          setSubmitting(false);
        }
      },
      (errMsg) => {
        setError(errMsg || "Failed to generate qr");
        setSubmitting(false);
      }
    );
  };

  if (!visible) return null;

  return (
    <SushiModal visible={visible} isCentered onClose={onClose}>
      <FlexColumn className={styles?.modalBody}>
        <div className={styles?.headerRow}>
          <span className={styles?.title}>Generate QR</span>
          <span className={styles?.close} onClick={onClose}>
            ✕
          </span>
        </div>

        {item?.product_name ? (
          <span className={styles?.subTitle}>{item.product_name}</span>
        ) : null}

        <div className={styles?.field}>
          <Datepicker
            wrapperClassName="datepickerFullWidth"
            key={`packing-date-${viewDate.getFullYear()}-${viewDate.getMonth()}`}
            dateFormat="MMMM d, yyyy"
            showMonthDropdown={false}
            showYearDropdown={false}
            selected={packedOnDate}
            openToDate={viewDate}
            open={pickerOpen}
            onInputClick={() => setPickerOpen(true)}
            onClickOutside={() => setPickerOpen(false)}
            onChange={(val) => {
              setPackedOnDate(val);
              setPickerOpen(false);
            }}
            minDate={packingMinDate}
            maxDate={packingMaxDate}
            customInput={<FloatingDateInput label="Packing date" />}
            renderCustomHeader={({
              date,
              changeYear,
              changeMonth,
            }) => (
              <div className={styles?.calHeader}>
                <Dropdown
                  ariaLabel="Select month"
                  value={date.getMonth()}
                  options={monthOptions}
                  onChange={(v) => {
                    const m = Number(v);
                    const next = new Date(viewDate);
                    next.setMonth(m);
                    setViewDate(next);
                    changeMonth(m);
                  }}
                />
                <Dropdown
                  ariaLabel="Select year"
                  value={date.getFullYear()}
                  options={yearOptions}
                  onChange={(v) => {
                    const y = Number(v);
                    const next = new Date(viewDate);
                    next.setFullYear(y);
                    setViewDate(next);
                    changeYear(y);
                  }}
                />
              </div>
            )}
          />
        </div>

        {variantOptions.length > 1 && (
          <div className={styles?.field}>
            <FloatingSelect
              value={selectedVariantIdx}
              onChange={(v) => setSelectedVariantIdx(Number(v))}
              options={variantOptions}
              label="Variant"
            />
          </div>
        )}

        <div className={styles?.field}>
          <div className={styles?.radioRow}>
            <span className={styles?.radioLabel}>Format</span>
            <div className={styles?.radioGroup}>
              {FORMAT_OPTIONS.map((opt) => (
                <label key={opt.value} className={styles?.radioOption}>
                  <input
                    type="radio"
                    name="downloadFormat"
                    value={opt.value}
                    checked={downloadFormat === opt.value}
                    onChange={() => setDownloadFormat(opt.value)}
                    className={styles?.radioInput}
                  />
                  <span className={styles?.radioCircle} />
                  <span className={styles?.radioText}>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {error ? <span className={styles?.errorText}>{error}</span> : null}

        <span className={styles?.disclaimerText}>
          For internal use only. This label must not be used as a substitute for mandatory labelling requirements.
        </span>

        <PrimaryButton
          className={`${styles?.cta} ${!isValid || submitting ? styles?.ctaDisabled : ""} text-white`}
          onClick={handleGenerate}
          disabled={!isValid || submitting}
        >
          {submitting ? "Generating..." : "Generate & Download"}
        </PrimaryButton>
      </FlexColumn>
    </SushiModal>
  );
};

export default GenerateUpcModal;
